import { Theme } from '@mui/material';
import { borderBottom, textTransform } from '@mui/system';
import { Colors } from '@sweatpals/react-ui-kit';

export const eventTicketCardStyles = (theme: Theme) => ({
	ticket: {
		position: 'relative',
		borderRadius: '16px',
		minHeight: '234px',
		width: '886px',
		display: 'flex',

		'@media screen and (max-width: 1200px)': {
			width: '330px',
			boxShadow: '0 13px 23px #00000040',
			padding: '1px',

			'&:before': {
				content: '""',
				position: 'absolute',
				inset: 0,
				borderRadius: '16px',
				border: '1px solid transparent',
				background: 'linear-gradient(144.19deg, rgba(255, 255, 255, 0.2) -2.52%, rgba(255, 255, 255, 0) 55.86%) border-box',
				mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
				maskComposite: 'exclude',
			}
		},

		'@media screen and (max-width: 500px)': {
			width: '100%',
			maxWidth: '330px',
		},
	},
	ticketBackground: {
		width: '100%',
		height: '100%',
		position: 'absolute',
		overflow: 'hidden',
		borderRadius: '16px',
		top: 0,
		left: 0,

		'& img': {
			position: 'absolute',
			top: 0,
			left: 0,
			width: '679px',
			height: '410px',
			filter: 'saturate(3) blur(100px)',
			clipPath: 'url(#ticket-background-path)',

			'@media screen and (max-width: 1200px)': {
				top: '-52px',
				height: '345px',
				width: '100%',
				transform: 'rotate(0deg)',
				clipPath: 'unset',
			},
		},
	},

	ticketBorder: {
		width: '100%',
		height: '100%',
		position: 'absolute',
		borderRadius: '16px',
		top: 0,
		left: 0,
		backgroundImage: 'linear-gradient(121.94deg, rgba(255, 255, 255, 0.2) 3.88%, rgba(255, 255, 255, 0) 75.33%)',
		clipPath: 'url(#ticket-border-path)',

		'@media screen and (max-width: 1200px)': {
			display: 'none',
		},
	},
	ticketContent: {
		position: 'relative',
		display: 'flex',
		background: '#00000080',
		clipPath: 'url(#ticket-background-path)',
		width: '100%',
		overflow: 'hidden',
		borderRadius: '16px',
		gap: '16px',

		'@media screen and (max-width: 1200px)': {
			flexDirection: 'column',
			gap: '12px',
			clipPath: 'unset',
		},
	},
	ticketInfo: {
		width: { xs: '100%', lg: '172px' },
		display: 'flex',
		flexDirection: { xs: 'column-reverse', lg: 'column' },
		py: { xs: '13px', lg: '18px' },
		px: { xs: '16px', lg: 0 },
		gap: { xs: '16px', lg: 0 },
	},
	mainTicketInfo: {
		flex: 1,
		display: 'flex',
		flexDirection: { xs: 'row', lg: 'column' },
		justifyContent: { xs: 'space-between', lg: 'unset' },
		alignItems: { xs: 'center', lg: 'unset' },
		gap: '5px',
	},
	ticketTitle: {
		fontFamily: 'Montserrat',
		fontSize: '12px',
		fontWeight: 600,
		lineHeight: '8px',
		color: '#FFFFFFB2',
	},
	ticketText: {
		color: '#FFFFFF',
		fontFamily: 'Montserrat',
		fontSize: '12px',
		fontWeight: 500,
		lineHeight: '15px',
	},
	upgradeTicketButton: {
		width: 'fit-content',
		p: '6px 12px',
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		color: Colors.white,
		fontFamily: 'Montserrat',
		fontSize: '13px',
		fontWeight: 700,
		lineHeight: '120%',
		borderColor: Colors['484848'],
		backgroundColor: Colors['343434'],

		'&:hover': {
			backgroundColor: Colors['484848'],
		},
	},
	ticketSecureSpotDescription: {
		color: Colors.white_a90,
		fontFamily: 'Montserrat',
		fontSize: '12px',
		fontWeight: 600,
		lineHeight: '15px',
	},
	ticketSecureSpotButton: {
		p: '8px 16px',
		fontFamily: 'Montserrat',
		fontSize: '14px',
		fontWeight: 600,
		lineHeight: '112%',
		minWidth: 'fit-content',
	},
	eventInfo: {
		flex: 1,
		backgroundColor: '#00000080',
		px: '20px',
		pt: '16px',
		display: 'flex',
		flexDirection: 'column',
		gap: '12px',
		overflow: 'hidden',
		justifyContent: { xs: 'unset', lg: 'space-between' },
	},
	eventName: {
		fontFamily: 'Integral CF, sans-serif',
		fontSize: '18px',
		fontWeight: 700,
		lineHeight: '22px',
		color: '#FFFFFF',
	},
	eventInfoItem: {
		display: 'flex',
		alignItems: { xs: 'center', lg: 'flex-start' },
		gap: '8px',

		'& >svg': {
			width: '20px',
			height: '20px',
			color: '#FFFFFFB2',
		},
	},
	ticketQrCode: (transferred?: boolean, fullName?: string) => ({
		width: { xs: '100%', lg: '290px' },
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		position: 'relative',
		py: '10px',

		...(transferred ? {
			'&:before': {
				content: '""',
				position: 'absolute',
				left: 0,
				top: 0,
				right: 0,
				bottom: 0,
				borderRadius: '16px',
				background: 'linear-gradient(270deg, rgba(0, 0, 0, 0.70) 84.03%, rgba(0, 0, 0, 0.00) 100%)',
				backdropFilter: 'blur(6px)',
			},

			'&:after': {
				content: `"This ticket was  transferred to ${fullName}"`,
				position: 'absolute',
				left: '50%',
				top: '50%',
				transform: 'translate(-50%, -50%)',
				color: 'white',
				fontFamily: 'Montserrat',
				fontSize: '15px',
				fontWeight: 500,
				textAlign: 'center',
			},
		} : {}),
	}),
	ticketIdText: {
		fontFamily: 'Montserrat',
		fontSize: '14px',
		fontWeight: 600,
		lineHeight: '14px',
		color: '#909090',
	},
	ticketQrCodeText: {
		width: '100%',
		textAlign: 'center',
		fontFamily: 'Montserrat',
		fontSize: '12px',
		fontWeight: 600,
		lineHeight: '15px',
		color: '#FFFFFFCC',
	},
	manageTicketSelect: {
		'&.MuiInputBase-root': {
			pl: '12px',
			borderRadius: '100px',
			minHeight: '36px',
			backgroundColor: Colors['343434'],

			'& .MuiSelect-select': {
				p: '6px',
				height: '14px',

				'& .MuiTypography-root': {
					color: Colors.white,
					fontSize: '13px',
					fontWeight: '700',
				},
			},

			'& .MuiSelect-icon': {
				color: Colors.white,
				right: '12px',
				width: '18px',
			},

			'& .MuiOutlinedInput-notchedOutline': {
				border: `1px solid ${Colors.white_a10}`,
				borderRadius: '100px',
			},
		},
	},
	manageTicketSelectPaper: {
		borderRadius: '8px',

		'& .MuiList-root': {
			py: '0',
		},
	},
	manageTicketSelectItem: {
		p: '10px 16px',
		gap: '10px',

		'&:not(:last-child)': {
			borderBottom: `1px solid ${Colors.white_a5}`,
		},
	},
	manageTicketSelectItemText: {
		'&.MuiTypography-root': {
			fontFamily: 'Montserrat',
			fontSize: '16px',
			fontWeight: '700',
		},
	},
});
